import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { budgetService } from '../services/budgetService';
import { CompanyInfo, BudgetItem } from '../types';
import {
  FileText,
  Euro,
  Plus,
  Trash2,
  Download,
  AlertCircle,
  CheckCircle,
  Calendar,
} from 'lucide-react';

const BudgetGenerator: React.FC = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Información de la empresa
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    userId: user?.uid || '',
    companyName: '',
    taxId: '',
    address: '',
    city: '',
    postalCode: '',
    province: 'Murcia',
    country: 'ES',
    phone: '',
    email: user?.email || '',
    website: '',
    logoUrl: '',
    bankAccount: '',
    bankName: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  // Información del paciente
  const [patientInfo, setPatientInfo] = useState({
    name: '',
    surname: '',
    dni: '',
    birthDate: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
  });

  // Items del presupuesto
  const [items, setItems] = useState<
    Omit<BudgetItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>[]
  >([
    {
      description: 'Prueba médica genética',
      quantity: 1,
      unitPrice: 0.0,
      discount: 0,
      taxRate: 0.0, // Exento para asistencia sanitaria
    },
  ]);

  // Opciones adicionales
  const [validityDays, setValidityDays] = useState(30);
  const [medicalJustification, setMedicalJustification] = useState('');
  const [paymentConditions, setPaymentConditions] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');

  // Validación
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    if (user?.email) {
      setCompanyInfo(prev => ({
        ...prev,
        email: user.email || '',
      }));
    }
  }, [user?.email]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!companyInfo.companyName.trim()) {
      errors.companyName = 'El nombre de la empresa es obligatorio';
    }
    if (!companyInfo.taxId.trim()) {
      errors.taxId = 'El NIF/CIF es obligatorio';
    }
    if (!companyInfo.address.trim()) {
      errors.address = 'La dirección es obligatoria';
    }
    if (!companyInfo.city.trim()) {
      errors.city = 'La ciudad es obligatoria';
    }
    if (!companyInfo.postalCode.trim()) {
      errors.postalCode = 'El código postal es obligatorio';
    }
    if (!companyInfo.phone.trim()) {
      errors.phone = 'El teléfono es obligatorio';
    }
    if (!patientInfo.name.trim()) {
      errors.patientName = 'El nombre del paciente es obligatorio';
    }
    if (!patientInfo.surname.trim()) {
      errors.patientSurname = 'El apellido del paciente es obligatorio';
    }
    if (!patientInfo.dni.trim()) {
      errors.patientDni = 'El DNI del paciente es obligatorio';
    }
    if (items.length === 0) {
      errors.items = 'Debe haber al menos un item en el presupuesto';
    }
    items.forEach((item, index) => {
      if (!item.description.trim()) {
        errors[`itemDescription${index}`] = 'La descripción es obligatoria';
      }
      if (item.quantity <= 0) {
        errors[`itemQuantity${index}`] = 'La cantidad debe ser mayor a 0';
      }
      if (item.unitPrice < 0) {
        errors[`itemPrice${index}`] = 'El precio no puede ser negativo';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unitPrice: 0.0,
        discount: 0,
        taxRate: 0.0,
      },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (
    index: number,
    field: keyof Omit<BudgetItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>,
    value: any
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const generateBudget = async () => {
    if (!validateForm()) {
      setError('Por favor, complete todos los campos obligatorios');
      return;
    }

    if (!user?.uid) {
      setError('No se pudo obtener la información del usuario');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Crear el presupuesto
      const budget = await budgetService.createBudget(
        user.uid,
        companyInfo,
        patientInfo,
        items,
        {
          validityDays,
          medicalJustification: medicalJustification.trim() || undefined,
          paymentConditions: paymentConditions.trim() || undefined,
          notes: notes.trim() || undefined,
          terms: terms.trim() || undefined,
        }
      );

      // Guardar presupuesto en Firestore
      const savedBudget = await budgetService.saveBudget(budget);

      // Generar PDF
      const pdfBlob = await budgetService.generateBudgetPDF(savedBudget);
      setSuccess(`Presupuesto ${savedBudget.budgetNumber} generado y guardado exitosamente`);

      // Descargar automáticamente
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Presupuesto_${savedBudget.budgetNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generando presupuesto:', err);
      setError(
        `Error al generar el presupuesto: ${
          err instanceof Error ? err.message : 'Error desconocido'
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl shadow-soft p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-gentle">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary-700">
                Generar Presupuesto
              </h1>
              <p className="text-pastel-gray-dark text-sm">
                Presupuesto profesional para pruebas médicas conforme a normativa de Murcia
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Información de la Empresa */}
          <section>
            <h2 className="text-xl font-bold text-primary-700 mb-4 flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Datos de la Empresa</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Nombre de la Empresa *
                </label>
                <input
                  type="text"
                  value={companyInfo.companyName}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, companyName: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.companyName ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.companyName && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.companyName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  NIF/CIF *
                </label>
                <input
                  type="text"
                  value={companyInfo.taxId}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, taxId: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.taxId ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.taxId && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.taxId}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Dirección *
                </label>
                <input
                  type="text"
                  value={companyInfo.address}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, address: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.address ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.address && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.address}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Ciudad *
                </label>
                <input
                  type="text"
                  value={companyInfo.city}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, city: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.city ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.city && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.city}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Código Postal *
                </label>
                <input
                  type="text"
                  value={companyInfo.postalCode}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, postalCode: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.postalCode ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.postalCode && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.postalCode}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Provincia
                </label>
                <input
                  type="text"
                  value={companyInfo.province}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, province: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="text"
                  value={companyInfo.phone}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, phone: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.phone ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.phone && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={companyInfo.email}
                  onChange={e =>
                    setCompanyInfo({ ...companyInfo, email: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </section>

          {/* Información del Paciente */}
          <section>
            <h2 className="text-xl font-bold text-primary-700 mb-4 flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Datos del Paciente</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={patientInfo.name}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, name: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.patientName ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.patientName && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.patientName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Apellidos *
                </label>
                <input
                  type="text"
                  value={patientInfo.surname}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, surname: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.patientSurname ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.patientSurname && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.patientSurname}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  DNI *
                </label>
                <input
                  type="text"
                  value={patientInfo.dni}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, dni: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.patientDni ? 'border-red-500' : 'border-pastel-gray-light'
                  }`}
                />
                {validationErrors.patientDni && (
                  <p className="text-red-600 text-xs mt-1">
                    {validationErrors.patientDni}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Fecha de Nacimiento
                </label>
                <input
                  type="date"
                  value={patientInfo.birthDate}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, birthDate: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Dirección
                </label>
                <input
                  type="text"
                  value={patientInfo.address}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, address: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Ciudad
                </label>
                <input
                  type="text"
                  value={patientInfo.city}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, city: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Código Postal
                </label>
                <input
                  type="text"
                  value={patientInfo.postalCode}
                  onChange={e =>
                    setPatientInfo({ ...patientInfo, postalCode: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </section>

          {/* Items del Presupuesto */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-primary-700 flex items-center space-x-2">
                <Euro className="h-5 w-5" />
                <span>Pruebas y Servicios</span>
              </h2>
              <button
                onClick={addItem}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Añadir Item</span>
              </button>
            </div>
            <div className="space-y-4">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border border-pastel-gray-light rounded-xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-medium text-primary-700">
                      Item {index + 1}
                    </h3>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-primary-700 mb-2">
                        Descripción *
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={e =>
                          updateItem(index, 'description', e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          validationErrors[`itemDescription${index}`]
                            ? 'border-red-500'
                            : 'border-pastel-gray-light'
                        }`}
                        placeholder="Ej: Análisis genético completo"
                      />
                      {validationErrors[`itemDescription${index}`] && (
                        <p className="text-red-600 text-xs mt-1">
                          {validationErrors[`itemDescription${index}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-700 mb-2">
                        Cantidad *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={e =>
                          updateItem(index, 'quantity', parseInt(e.target.value) || 1)
                        }
                        className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          validationErrors[`itemQuantity${index}`]
                            ? 'border-red-500'
                            : 'border-pastel-gray-light'
                        }`}
                      />
                      {validationErrors[`itemQuantity${index}`] && (
                        <p className="text-red-600 text-xs mt-1">
                          {validationErrors[`itemQuantity${index}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-700 mb-2">
                        Precio Unit. (€) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={e =>
                          updateItem(
                            index,
                            'unitPrice',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          validationErrors[`itemPrice${index}`]
                            ? 'border-red-500'
                            : 'border-pastel-gray-light'
                        }`}
                      />
                      {validationErrors[`itemPrice${index}`] && (
                        <p className="text-red-600 text-xs mt-1">
                          {validationErrors[`itemPrice${index}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary-700 mb-2">
                        Descuento (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount || 0}
                        onChange={e =>
                          updateItem(
                            index,
                            'discount',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-primary-700 mb-2">
                      IVA (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={item.taxRate}
                      onChange={e =>
                        updateItem(
                          index,
                          'taxRate',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0% (exento para asistencia sanitaria)"
                    />
                    <p className="text-xs text-pastel-gray-dark mt-1">
                      Nota: La asistencia sanitaria está exenta de IVA según Art. 20.1.9º Ley 37/1992
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Opciones Adicionales */}
          <section>
            <h2 className="text-xl font-bold text-primary-700 mb-4 flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Opciones Adicionales</span>
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Validez del Presupuesto (días)
                </label>
                <input
                  type="number"
                  min="1"
                  value={validityDays}
                  onChange={e => setValidityDays(parseInt(e.target.value) || 30)}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Justificación Médica
                </label>
                <textarea
                  value={medicalJustification}
                  onChange={e => setMedicalJustification(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Descripción de la necesidad médica de la prueba..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Condiciones de Pago
                </label>
                <textarea
                  value={paymentConditions}
                  onChange={e => setPaymentConditions(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Ej: Pago al contado, transferencia bancaria, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Términos y Condiciones
                </label>
                <textarea
                  value={terms}
                  onChange={e => setTerms(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-pastel-gray-light rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Términos legales y condiciones del servicio..."
                />
              </div>
            </div>
          </section>

          {/* Botones de Acción */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-pastel-gray-light">
            <button
              onClick={generateBudget}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 shadow-gentle hover:shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5" />
                  <span>Generar y Descargar PDF</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetGenerator;

